import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { BrandMark } from '@/components/BrandLogo';
import SiteLegalLinks from '@/components/SiteLegalLinks';
import {
  OPERATOR_LEGAL_NAME,
  OPERATOR_COMPANY_NUMBER,
  PRODUCT_NAME,
} from '@/legal/companyInfo';
import { toast } from 'sonner';
import adminRestaurantBackground from '@/assets/admin-restaurant-background.png';

const PLAN_FEATURES = [
  'Branded booking widget for your website',
  'Admin dashboard + tablet host-stand view',
  'Deposit collection and payment links',
  'Automated email confirmations and reminders',
  'Google Calendar sync',
  'Guest notes, allergies, and team PIN access',
];

export default function AdminRegister() {
  const navigate = useNavigate();
  const { register, isAuthenticated, user, loading: authLoading, isTenantUser } = useAuth();

  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    restaurant_name: '',
    restaurant_phone: '',
    restaurant_address: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) return;
    if (user.role === 'platform_admin') navigate('/platform/dashboard', { replace: true });
    else if (isTenantUser) navigate('/admin/dashboard', { replace: true });
  }, [authLoading, isAuthenticated, user, isTenantUser, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!termsAccepted || !privacyAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy to register.');
      return;
    }
    setLoading(true);
    try {
      const result = await register({
        ...form,
        agree_terms: termsAccepted,
        agree_privacy: privacyAccepted,
      });
      if (result?.waitlist) {
        toast.success(result.message || 'Thanks — you are on the waiting list.');
        navigate('/admin/login');
        return;
      }
      toast.success('Account created! Welcome to DineSpot.');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <img src={adminRestaurantBackground} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/78 to-slate-950/35" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_48%_36%,rgba(245,158,11,0.18),transparent_34%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-950/90 to-transparent" />

      <div className="relative z-10 p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      <div className="relative z-10 flex min-h-[calc(100vh-88px)] items-center justify-center px-6 pb-10">
        <div className="grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_440px]">

          {/* Left panel */}
          <section className="hidden animate-scale-in text-white lg:block">
            <BrandMark size="lg" className="mb-8 border-white/25 shadow-2xl shadow-amber-950/40 backdrop-blur-md" />
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-amber-100/80">Restaurant Console</p>
            <h1 className="max-w-2xl font-heading text-5xl font-bold leading-tight tracking-tight text-white">
              A calmer way to run a busy dining room.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-white/72">
              Manage bookings, deposits, guest details, and service flow from one premium workspace built for restaurants.
            </p>
            <div className="mt-10 grid max-w-xl gap-4 sm:grid-cols-3">
              {['Live booking control', 'Deposit tracking', 'Calendar sync'].map((item) => (
                <div key={item} className="rounded-2xl border border-white/15 bg-white/10 p-4 text-sm text-white/80 backdrop-blur-md">
                  <CheckCircle2 className="mb-3 h-4 w-4 text-amber-200" />
                  {item}
                </div>
              ))}
            </div>
          </section>

          {/* Right panel */}
          <div className="w-full animate-scale-in">
            <div className="mb-6 text-center lg:hidden">
              <BrandMark size="lg" className="mx-auto mb-5 border-white/25 shadow-2xl shadow-amber-950/40 backdrop-blur-md" />
            </div>

            <Card className="border-white/15 bg-white/95 text-slate-950 shadow-2xl shadow-black/40 backdrop-blur-xl">
              <CardHeader className="space-y-1 pb-4">
                <h2 className="font-heading text-2xl font-semibold tracking-tight">
                  {showForm ? 'Your details' : 'Get started'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {showForm
                    ? 'Complete your account setup below.'
                    : 'Start your 14-day free trial — no card required today.'}
                </p>
              </CardHeader>

              <CardContent>
                {/* ── Pricing card ── */}
                {!showForm && (
                  <div className="rounded-xl border border-slate-200 bg-slate-950 p-5 text-white">
                    <p className="text-xs font-semibold uppercase tracking-widest text-amber-400">Pro Plan</p>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold">£39</span>
                      <span className="text-base text-white/60">/ month</span>
                    </div>
                    <p className="mt-1 text-sm text-white/55">
                      14-day free trial · no card required today
                    </p>

                    <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-400/80">What's included</p>
                      {PLAN_FEATURES.map((f) => (
                        <div key={f} className="flex items-start gap-2.5 text-sm text-white/80">
                          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                          {f}
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-xs text-white/50 leading-relaxed">
                      One-month rolling contract · cancel anytime · no setup fee
                    </div>

                    <Button
                      className="mt-5 w-full gap-2 bg-amber-400 text-slate-950 font-semibold hover:bg-amber-300"
                      onClick={() => setShowForm(true)}
                      data-testid="pricing-cta"
                    >
                      Start free trial
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* ── Sign-up form ── */}
                {showForm && (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft className="h-3 w-3" />
                      Back to plan
                    </button>

                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Your Name</Label>
                      <Input
                        id="reg-name"
                        placeholder="John Doe"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        data-testid="register-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="you@restaurant.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        data-testid="register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Min. 6 characters"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="register-password"
                      />
                    </div>

                    <div className="border-t border-border/50 pt-2">
                      <p className="mb-3 text-sm text-muted-foreground">Restaurant Details</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reg-restaurant">Restaurant Name</Label>
                      <Input
                        id="reg-restaurant"
                        placeholder="The Golden Fork"
                        value={form.restaurant_name}
                        onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })}
                        required
                        data-testid="register-restaurant-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-phone">Phone</Label>
                      <Input
                        id="reg-phone"
                        type="tel"
                        placeholder="+44 (0) 7700 000000"
                        value={form.restaurant_phone}
                        onChange={(e) => setForm({ ...form, restaurant_phone: e.target.value })}
                        required
                        data-testid="register-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-address">Address</Label>
                      <Input
                        id="reg-address"
                        placeholder="123 High Street, London"
                        value={form.restaurant_address}
                        onChange={(e) => setForm({ ...form, restaurant_address: e.target.value })}
                        required
                        data-testid="register-address"
                      />
                    </div>

                    <div className="space-y-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="reg-terms"
                          checked={termsAccepted}
                          onCheckedChange={(v) => setTermsAccepted(v === true)}
                          data-testid="register-terms-checkbox"
                        />
                        <label htmlFor="reg-terms" className="text-xs leading-relaxed text-muted-foreground cursor-pointer">
                          I have read and agree to the{' '}
                          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline">
                            Terms of Service
                          </Link>.
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="reg-privacy"
                          checked={privacyAccepted}
                          onCheckedChange={(v) => setPrivacyAccepted(v === true)}
                          data-testid="register-privacy-checkbox"
                        />
                        <label htmlFor="reg-privacy" className="text-xs leading-relaxed text-muted-foreground cursor-pointer">
                          I have read and agree to the{' '}
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline">
                            Privacy Policy
                          </Link>.
                        </label>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || !termsAccepted || !privacyAccepted}
                      data-testid="register-submit"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <footer className="relative z-10 border-t border-white/10 px-6 py-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4">
          <SiteLegalLinks variant="dark" />
          <p className="text-center text-xs text-white/50">
            © {new Date().getFullYear()} {PRODUCT_NAME} · {OPERATOR_LEGAL_NAME} · Co. no. {OPERATOR_COMPANY_NUMBER}
          </p>
        </div>
      </footer>
    </div>
  );
}
